import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Mail, Building } from 'lucide-react';
import { AutelionLogo } from '@/components/common/AutelionLogo';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface InvitationDetails {
  requires_auth?: boolean;
  auth_type?: 'signin' | 'signup';
  email?: string;
  org_name?: string;
  role?: string;
  message?: string;
  success?: boolean;
  redirect?: string;
  error?: string;
}

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const headers: Record<string, string> = {};
      
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.access_token) {
        headers['Authorization'] = `Bearer ${currentSession.access_token}`;
      }

      const response = await supabase.functions.invoke('accept-invitation', {
        body: { token },
        headers,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data as InvitationDetails;
    },
    onSuccess: (data) => {
      setInvitationDetails(data);
      
      if (data.success && data.redirect) {
        // Invalidate profile cache so ProtectedRoute gets fresh data with org_id
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        queryClient.invalidateQueries({ queryKey: ['organization'] });
        
        // Successfully accepted - redirect to dashboard
        setTimeout(() => {
          navigate(data.redirect || '/dashboard');
        }, 2000);
      } else if (data.requires_auth) {
        // Need to authenticate
        setEmail(data.email || '');
      }
    },
    onError: (error: Error) => {
      setInvitationDetails({ error: error.message });
    },
  });

  // Check invitation on mount
  useEffect(() => {
    if (token) {
      acceptMutation.mutate();
    }
  }, [token, session]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsProcessing(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setAuthError(error.message);
        setIsProcessing(false);
        return;
      }

      // Re-try accepting the invitation after login
      setTimeout(() => {
        acceptMutation.mutate();
      }, 500);
    } catch (err) {
      setAuthError('An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsProcessing(true);

    if (password.length < 6) {
      setAuthError('Password must be at least 6 characters');
      setIsProcessing(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/accept-invitation?token=${token}`,
          data: {
            name: name,
          },
        },
      });

      if (error) {
        setAuthError(error.message);
        setIsProcessing(false);
        return;
      }

      // Show confirmation message
      setInvitationDetails({
        message: 'Please check your email to confirm your account, then return to this page.',
      });
      setIsProcessing(false);
    } catch (err) {
      setAuthError('An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="w-full max-w-md bg-card/95 dark:bg-card backdrop-blur border border-border">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 mx-auto text-danger mb-4" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>
              No invitation token was provided
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (acceptMutation.isPending && !invitationDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="w-full max-w-md bg-card/95 dark:bg-card backdrop-blur border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitationDetails?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="w-full max-w-md bg-card/95 dark:bg-card backdrop-blur border border-border">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 mx-auto text-danger mb-4" />
            <CardTitle>Invitation Error</CardTitle>
            <CardDescription>
              {invitationDetails.error}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitationDetails?.success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="w-full max-w-md bg-card/95 dark:bg-card backdrop-blur border border-border">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-success mb-4" />
            <CardTitle>Welcome to {invitationDetails.org_name}!</CardTitle>
            <CardDescription>
              You've successfully joined the organization. Redirecting to dashboard...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitationDetails?.requires_auth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="w-full max-w-md bg-card/95 dark:bg-card backdrop-blur border border-border">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AutelionLogo className="h-10" />
            </div>
            <CardTitle>Join {invitationDetails.org_name}</CardTitle>
            <CardDescription className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <Building className="h-4 w-4" />
                <span>You've been invited as a <strong className="capitalize">{invitationDetails.role}</strong></span>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs">
                <Mail className="h-3 w-3" />
                <span>{invitationDetails.email}</span>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {authError && (
              <Alert variant="danger" className="mb-4">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            
            <Tabs defaultValue={invitationDetails.auth_type} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="signin" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100">Sign In</TabsTrigger>
                <TabsTrigger value="signup" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In & Accept Invitation'
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a password (min 6 characters)"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isProcessing}>
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account & Accept Invitation'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <Card className="w-full max-w-md bg-card/95 dark:bg-card backdrop-blur border border-border">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Processing invitation...</p>
        </CardContent>
      </Card>
    </div>
  );
}
